import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useRecoilValue } from "recoil";
import { getCommonFacilityListAsync } from "src/api/building/building-api";
import { CommonFacilityListModel, CommonFacilityListParams } from "src/api/building/building-types";
import { useApiOperation } from "src/api/hooks";
import { PageMeta } from "src/api/public-types";
import { BaseButton, BaseRadio, BaseSelect, BaseToggle, BaseTooltip } from "src/components";
import { BaseAbstractModal } from "src/components/BaseAbstractModal";
import { PagePath } from "src/pages/product/details";
import { globalPartnerState } from "src/recoil/partners/atom";
import { CalendarSetting, FORM_NAMES } from "../../reservation-types";
import ReservationFacilityListModal from "./ReservationFacilityListModal";

type Props = {
  onClose: () => void;
};
type BuildingOptions = {
  value: string;
  label: string;
};

const FACILITY_OPTIONS = [
  {
    value: "",
    label: "전체",
  },
  {
    value: "MEETING_ROOM",
    label: "회의실",
  },
  {
    value: "DESK",
    label: "좌석",
  },
  {
    value: "REFRESH_ROOM",
    label: "편의시설",
  },
];

const defaultParams: CommonFacilityListParams = {
  page: 0,
  size: 9999,
  sort: {
    orders: [
      {
        property: "id",
        direction: "DESC",
      },
    ],
  },
  searchType: "FACILITY_NAME",
};

const CalendarModal = ({ onClose }: Props) => {
  const {
    control,
    setValue,
    handleSubmit,
    formState: { errors, defaultValues },
    watch,
    getValues,
  } = useForm<CalendarSetting>({
    defaultValues: {
      transportType: false,
      reservationName: "restricted", // restricted, private
      attendeeInfo: "restricted",
      companyName: "restricted",
      buildingIdList: "",
      floor: "",
      fixedBuilding: "",
      fixedFloor: "",
    },
  });
  const partner = useRecoilValue(globalPartnerState);
  const [facilityModal, setFacilityModal] = useState({ isOpen: false });
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [facilityList, setFacilityList] = useState<CommonFacilityListModel[]>([]);
  const [filteredFacilityList, setFilteredFacilityList] = useState<CommonFacilityListModel[]>(facilityList);
  const [buildingOptions, setBuildingOptions] = useState<BuildingOptions[]>([{ value: "", label: "전체" }]);
  const [floorOptions, setFloorOptions] = useState<BuildingOptions[]>([{ value: "", label: "전체" }]);
  const [selectedFacilityList, setSelectedFacilityList] = useState<CommonFacilityListModel[]>(filteredFacilityList);
  const { executeAsync: getFacilityList } = useApiOperation(getCommonFacilityListAsync);

  const onSubmit = (data: CalendarSetting) => {
    const addTransPortTypeData = data.transportType ? { ...data, transportType: "on" } : { ...data, transportType: "off" };
    const addSelectedFacilityIdList = { ...addTransPortTypeData, facilityIdList: selectedFacilityList.map((facility) => facility.id).join(",") };
    if (!data.buildingIdList && !data.commonFacilityTypeList) {
      addSelectedFacilityIdList.facilityIdList = "";
    }
    if (data.buildingIdList) {
      addSelectedFacilityIdList.fixedBuilding = "on";
    }
    if (data.floor) {
      addSelectedFacilityIdList.fixedFloor = "on";
    }
    const queryParams = new URLSearchParams(addSelectedFacilityIdList as Record<string, string>);

    console.log(addSelectedFacilityIdList);
    const pathWithQueryString = `${PagePath.reservation.calendar.replace(":partnerId", String(partner?.id))}?${queryParams.toString()}`;
    window.open(pathWithQueryString, "_blank");
    onClose();
  };
  const handleOpenFacilityModal = () => {
    setFacilityModal({ isOpen: true });
  };
  const handleCloseFacilityModal = () => {
    setFacilityModal({ isOpen: false });
  };
  const updateBuildingOptions = (newBuildings: BuildingOptions[]) => {
    setBuildingOptions((prev: BuildingOptions[]) => {
      const defaultOption = { value: "", label: "전체" };

      // 기존 옵션과 새로운 옵션을 합치고 중복 제거
      const allOptions = [...prev, ...newBuildings];
      const uniqueOptions = [
        defaultOption,
        ...Array.from(
          new Map(
            allOptions
              .filter((option) => option.value !== "") // "전체" 옵션 제외
              .map((option) => [option.value, option]),
          ).values(),
        ),
      ];
      return uniqueOptions;
    });
  };
  const applyFilters = () => {
    try {
      // 현재 폼 값 가져오기 (watch 대신 getValues 사용)
      const buildingIdList = getValues("buildingIdList");
      const commonFacilityTypeList = getValues("commonFacilityTypeList");
      const floorValue = getValues("floor");

      let filtered = [...facilityList];

      // 건물 ID로 필터링
      if (buildingIdList) {
        filtered = filtered.filter((item) => String(item.buildingId) === buildingIdList);
      }

      // 공용 공간 유형으로 필터링
      if (commonFacilityTypeList) {
        filtered = filtered.filter((item) => item.commonFacilityType === commonFacilityTypeList);
      }

      // 층 필터링
      if (floorValue) {
        filtered = filtered.filter((item) => {
          const floorNum = typeof item.floorNum === "string" ? parseInt(item.floorNum, 10) : item.floorNum || 0;
          const absoluteFloor = item.isGround ? floorNum : -floorNum;
          return String(absoluteFloor) === floorValue;
        });
      }

      // 상태 업데이트
      setFilteredFacilityList(filtered);
      setSelectedFacilityList(filtered);
      return filtered;
    } catch (error) {
      console.error("필터링 오류:", error);
      setFilteredFacilityList(facilityList);
      setSelectedFacilityList(facilityList);
      return facilityList;
    }
  };

  useEffect(() => {
    if (facilityList && facilityList.length > 0) {
      setFilteredFacilityList(facilityList);

      // 건물 옵션 업데이트
      const buildingsData = facilityList.map((item) => ({
        value: String(item.buildingId),
        label: item.buildingName || "알 수 없음",
      }));
      updateBuildingOptions(buildingsData);
    }
  }, []);

  useEffect(() => {
    if (buildingOptions.length === 2) setValue("buildingIdList", buildingOptions[1].value);
    updateFloorOptions(watch("buildingIdList"));
  }, [buildingOptions, setValue]);

  const updateFloorOptions = (buildingId: string) => {
    // 선택된 건물의 시설만 필터링
    const buildingFacilities = facilityList.filter((facility) => String(facility.buildingId) === buildingId);

    // 각 시설에 absoluteFloor 추가
    const facilitiesWithAbsoluteFloor = buildingFacilities.map((facility) => {
      const floorNum = typeof facility.floorNum === "string" ? parseInt(facility.floorNum, 10) : facility.floorNum || 0;

      // isGround가 true면 양수, false면 음수(지하)
      const absoluteFloor = facility.isGround ? floorNum : -floorNum;

      return { ...facility, absoluteFloor };
    });

    // absoluteFloor 기준으로 정렬하고 중복 제거
    const uniqueFloors = [
      ...new Set(
        facilitiesWithAbsoluteFloor
          .sort((a, b) => a.absoluteFloor - b.absoluteFloor) // 오름차순 정렬
          .map((facility) => facility.absoluteFloor)
          .filter((floor) => !!floor),
      ),
    ];

    // 층 옵션 생성
    const floorOptions = uniqueFloors.map((floor) => {
      // 양수면 "#F", 음수면 "B#" 형식으로 표시
      const label = floor > 0 ? `${floor}F` : `B${Math.abs(floor)}`;
      return { value: String(floor), label };
    });

    // 전체 옵션과 함께 층 옵션 설정
    setFloorOptions([{ value: "", label: "전체" }, ...floorOptions]);
  };

  useEffect(() => {
    fetchFacilityList(defaultParams);
  }, []);

  //공용공간 목록
  const fetchFacilityList = async (request: CommonFacilityListParams) => {
    const { data: facilities } = await getFacilityList(request);
    const result = facilities.data.content;
    const filteredFacilityLIst = result.filter((facility) => !facility.isDeleted);
    setFacilityList(filteredFacilityLIst);
    setFilteredFacilityList(filteredFacilityLIst);
    setSelectedFacilityList(filteredFacilityLIst);
    setPageMeta(facilities.meta.pageMeta);

    if (filteredFacilityLIst && filteredFacilityLIst.length > 0) {
      const buildingsData = filteredFacilityLIst.map((item) => ({
        value: String(item.buildingId),
        label: item.buildingName || "알 수 없음",
      }));
      updateBuildingOptions(buildingsData);
    }
  };
  return (
    <BaseAbstractModal isOpen={true} size="large">
      <form onSubmit={handleSubmit(onSubmit)}>
        <section className="base-abstract-modal__title flex-column flex-start border-bottom">
          <h1>공간 예약 현황 설정</h1>
        </section>
        <section className="base-abstract-modal__contents px30">
          <section className="base-abstract-modal__contents-subtitle">
            <h2>화면 이동 설정</h2>
          </section>
          <div className="d-flex mb20">
            <div className="flex-center minmax145 font14 font-weight-600 text-primary3">
              <p>자동 페이지 이동</p>
              <BaseTooltip
                touchIcon={"QUESTION"}
                className="ml4"
                contents={`터치를 지원하지 않는 모니터를 사용하실 경우 이 기능을 활성화하면 30초마다 자동으로 다음 페이지로 이동됩니다.\n
단, 설정 기능만 사용할 수 있으며, 스크롤이나 예약 상세 보기 기능은 제한됩니다.`}
              />
            </div>
            <div className="flex-center">
              <Controller
                control={control}
                name={"transportType"}
                render={({ field: { onChange, value, name } }) => {
                  return (
                    <BaseToggle
                      onChange={(checked: boolean) => {
                        onChange(checked);
                      }}
                      checked={value}
                      name={name}
                    />
                  );
                }}
              ></Controller>
            </div>
          </div>
        </section>
        <section className="base-abstract-modal__contents px30">
          <section className="base-abstract-modal__contents-subtitle mt0">
            <h2>예약 정보 보안 설정</h2>
          </section>
          <div className="d-flex h-40">
            <div className="flex-center minmax145 font14 font-weight-600 text-primary3">
              <p>예약명</p>
            </div>
            <div className="flex-center">
              <Controller
                control={control}
                name={FORM_NAMES.RESERVATION_NAME}
                render={({ field }) => {
                  return (
                    <div className="flex-row flex-center-start">
                      {[
                        { value: "public", label: "공개" },
                        { value: "restricted", label: "일부공개" },
                        { value: "private", label: "비공개" },
                      ].map((data, index) => (
                        <div className="flex-center" key={index}>
                          <BaseRadio
                            id={FORM_NAMES.RESERVATION_NAME + data.label}
                            name={FORM_NAMES.RESERVATION_NAME}
                            label={data.label}
                            onChange={(e) => {
                              if (e) {
                                setValue(FORM_NAMES.RESERVATION_NAME, data.value);
                              }
                            }}
                            checked={field.value === data.value}
                            className={data.value === "restricted" ? "" : "mr10"}
                          />
                          {data.value === "restricted" && (
                            <BaseTooltip
                              touchIcon={"QUESTION"}
                              preformatted
                              className="mr10 cursor-pointer"
                              contents={`첫 3글자를 제외하고 모두 가려집니다. \n예) 고객사 미팅 → 고객사****`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
            </div>
          </div>
          <div className="d-flex h-40">
            <div className="flex-center minmax145 font14 font-weight-600 text-primary3">
              <p>주최자/참석자 정보</p>
            </div>
            <div className="flex-center">
              <Controller
                control={control}
                name={FORM_NAMES.ATTENDEE_INFO}
                render={({ field }) => {
                  return (
                    <div className="flex-row flex-center-start">
                      {[
                        { value: "public", label: "공개" },
                        { value: "restricted", label: "일부공개" },
                        { value: "private", label: "비공개" },
                      ].map((data, index) => (
                        <div className="flex-center" key={index}>
                          <BaseRadio
                            key={index}
                            id={FORM_NAMES.ATTENDEE_INFO + data.label}
                            name={FORM_NAMES.ATTENDEE_INFO}
                            label={data.label}
                            onChange={(e) => {
                              if (e) {
                                setValue(FORM_NAMES.ATTENDEE_INFO, data.value);
                              }
                            }}
                            checked={field.value === data.value}
                            className={data.value === "restricted" ? "" : "mr10"}
                          />
                          {data.value === "restricted" && (
                            <BaseTooltip
                              touchIcon={"QUESTION"}
                              preformatted
                              className="mr10 cursor-pointer"
                              contents={`닉네임과 이메일의 일부분이 가려집니다.\n예) 김수미(example@email.com) → 김*미(ex*****@*********)`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
            </div>
          </div>
          <div className="d-flex h-40">
            <div className="flex-center minmax145 font14 font-weight-600 text-primary3">
              <p>소속 회사</p>
            </div>
            <div className="flex-center">
              <Controller
                control={control}
                name={FORM_NAMES.COMPANY_NAME}
                render={({ field }) => {
                  return (
                    <div className="flex-row flex-center-start">
                      {[
                        { value: "public", label: "공개" },
                        { value: "restricted", label: "일부공개" },
                        { value: "private", label: "비공개" },
                      ].map((data, index) => (
                        <div className="flex-center" key={index}>
                          <BaseRadio
                            key={index}
                            id={FORM_NAMES.COMPANY_NAME + data.label}
                            name={FORM_NAMES.COMPANY_NAME + data.label}
                            label={data.label}
                            onChange={(e) => {
                              if (e) {
                                setValue(FORM_NAMES.COMPANY_NAME, data.value);
                              }
                            }}
                            checked={field.value === data.value}
                            className={data.value === "restricted" ? "" : "mr10"}
                          />
                          {data.value === "restricted" && (
                            <BaseTooltip
                              touchIcon={"QUESTION"}
                              preformatted
                              className="mr10 cursor-pointer"
                              contents={`회사명의 첫 글자를 제외한 모든 글자가 가려집니다. \n예) 핀포인트 → 핀***`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  );
                }}
              />
            </div>
          </div>
          <section className="base-abstract-modal__contents-subtitle mt0">
            <h2>화면 표시 공간 설정</h2>
          </section>
          <div className="d-flex flex-start">
            <div className="d-flex h-40 mb10">
              <div className="flex-center minmax145 font14 font-weight-600 text-primary3">
                <p>건물 및 층</p>
              </div>
              <div className="flex-center">
                <Controller
                  control={control}
                  name={"buildingIdList"}
                  render={({ field }) => {
                    return (
                      <BaseSelect
                        className="minmax240 mr10"
                        stateOptions={buildingOptions}
                        value={watch("buildingIdList")}
                        placeholder="전체 건물"
                        menuPosition="fixed"
                        isDisabled={buildingOptions.length === 2}
                        setStateValue={(value: string) => {
                          setValue("buildingIdList", value, { shouldValidate: true });
                          setValue("floor", "");

                          // 건물 선택 시 층 옵션 업데이트
                          if (value) {
                            updateFloorOptions(value);
                          } else {
                            setFloorOptions([{ value: "", label: "전체" }]);
                          }

                          // 마지막에 applyFilters 호출 (getValues 사용)
                          applyFilters();
                        }}
                      />
                    );
                  }}
                />
              </div>
            </div>
            <div className="d-flex h-40 mb10">
              <div className="flex-center">
                <Controller
                  control={control}
                  name={"floor"}
                  render={({ field }) => {
                    return (
                      <BaseSelect
                        className="minmax240 mr5"
                        stateOptions={floorOptions}
                        value={getValues("floor")}
                        placeholder="전체 층"
                        menuPosition="fixed"
                        isDisabled={!getValues("buildingIdList")}
                        setStateValue={(value: string) => {
                          // 즉시 setValue 실행
                          setValue("floor", value, { shouldValidate: true });
                          // 필터 즉시 적용
                          applyFilters();
                        }}
                      />
                    );
                  }}
                />
              </div>
            </div>
          </div>

          <div className="d-flex h-40">
            <div className="flex-center minmax145 font14 font-weight-600 text-primary3">
              <p>공용공간</p>
            </div>
            <div>
              <div className="flex-center">
                <BaseButton
                  title={"선택"}
                  type="button"
                  disabled={!watch("buildingIdList") || selectedFacilityList?.length === 0}
                  className="color-white"
                  onClick={handleOpenFacilityModal}
                />
                {facilityList.length > 0 && (
                  <p className={`font14 pl10 ${selectedFacilityList?.length === 0 ? "text-red" : ""}`}>
                    {selectedFacilityList?.length}개가 선택되었습니다.
                  </p>
                )}

                {facilityModal.isOpen && (
                  <ReservationFacilityListModal
                    filteredFacilityList={filteredFacilityList}
                    selectedFacilityList={selectedFacilityList}
                    onClick={(data) => {
                      setSelectedFacilityList(data);
                    }}
                    onClose={handleCloseFacilityModal}
                  />
                )}
              </div>
            </div>
          </div>
        </section>
        <section className="base-abstract-modal__btn-wrap">
          <BaseButton title={"취소"} type="button" className="color-white" onClick={onClose} />
          <BaseButton title={"완료"} type="submit" disabled={selectedFacilityList && selectedFacilityList.length === 0} />
        </section>
      </form>
    </BaseAbstractModal>
  );
};

export default CalendarModal;
